import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import debounce from 'lodash/debounce';
import { connect } from 'pwa-helpers';
import { ActionStatus } from '../../../common/constants/action-status';
import { smallSpinner } from '../../../common/constants/api-constants';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { FormBaseComponent } from '../../base-component/forms/form-base-component';
import { resetSchoolResponse } from '../../user-profile';
import { fetchSchools } from '../../user-profile/thunks/fetch-schools.thunk';
import { getSchoolResponse, getSchoolStatus } from '../../user-profile/user-profile.selectors';
import { booleanConverter } from '../../../common/converters';
import { School } from '../../user-profile/user-profile.interface';

/**
 * Select component to allow user to select a Country option.
 * Country options are loaded via API
 */
@customElement('mhecomm-school-selector')
export class SchoolSelect extends connect(store)(FormBaseComponent) {
  static styles = [
    css`
      .dropdown-menu {
        display: none;
      }
      .dropdown-menu .list {
        max-height: 450px;
        overflow-y: scroll;
      }
      .spinner-container {
        display: none;
      }

      input.loading + .spinner-container {
        display: block;
      }

      .dropdown-item.selected {
        color: var(--theme-gray-900);
        background-color: var(--theme-gray-200);
      }

      .dropdown-menu:not(:has(div)) {
        border-width: 0;
      }

      .hide-error + .invalid-feedback {
        display: none !important;
      }
    `,
  ];

  @property()
  placeholder = '';

  @property({converter: booleanConverter})
  disabled = false;

  @property({converter: booleanConverter})
  requireTextInput = true;

  @property()
  required = '';

  @property()
  countryCode = '';

  @property()
  stateCode = '';

  @property()
  zipCode = '';

  @property()
  label = 'School Search';

  @property()
  segment?: string;

  @state()
  suggestions: School[] = [];

  @state()
  suggestionStatus = ActionStatus.idle;

  @state()
  hasNextSuggestions: boolean = false;

  @state()
  private currentFocusOption = '';

  @state()
  private dropdownMenuVisibility: boolean = false;

  @state()
  private searchInput: string = '';

  @state()
  private searchText: string = '';

  @state()
  private selected?: School;

  @state()
  private lastIndex: number = 0;

  @state()
  private preview: any = null;

  @state()
  private dropdownVisibilityTimeout;

  @state()
  private mouseOverMenu = false;

  private selectFirstOnLoad: boolean = false;

  id = 'schoolSearch';

  constructor() {
    super();
    this.addEventListener(
      'input',
      debounce(() => this.onInputChange(), 500)
    );
  }

  get inputEl() {
    return this.shadowRoot!.querySelector(`#${this.id}`) as HTMLInputElement;
  }

  get dropdownMenuEl() {
    return this.shadowRoot?.querySelector('.dropdown .dropdown-menu');
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('dropdownMenuVisibility')) {
      if (this.dropdownMenuVisibility) {
        this.dropdownMenuEl?.classList.add('show');
      } else {
        this.dropdownMenuEl?.classList.add('opacity-0');
        setTimeout(() => {
          this.dropdownMenuEl?.classList.remove('show', 'opacity-0');
        }, 500);
      }
    }
    if (_changedProperties.has('suggestions')) {
      this.onSuggestionsLoaded();
    }
  }

  stateChanged(state: StoreState) {
    this.suggestionStatus = getSchoolStatus(state);
    if (this.searchInput?.length === 0 && !this.requireTextInput && !this.disabled && this.suggestionStatus === ActionStatus.idle) {
      this.clearSuggestions();
      this.dispatchSearch(false);
    }

    if (this.suggestionStatus === ActionStatus.succeeded) {
      const response = getSchoolResponse(state);
      if (response?.content) {
        this.suggestions = this.hasNextSuggestions ? [...this.suggestions, ...response.content] : response.content;
      }

      this.hasNextSuggestions = response.totalElements > this.suggestions.length;
    }
  }

  renderComp() {
    return html`
      <form novalidate @submit=${this.submit}>
        <label for="${this.id}" class="form-label">${this.label} </label>
        <div
          class="input-group ${this.dropdownMenuVisibility ? 'hide-error' : ''}"
          @focusin=${(e) => this.showDropDownMenu(e)}
          @focusout=${this.onFocusOut}
        >
          <input
            class="form-control text-capitalize"
            part="us-autocomplete-pro-address-input"
            id="${this.id}"
            type="text"
            placeholder=${this.placeholder}
            .value=${this.searchInput}
            ?disabled=${this.disabled}
            ?required=${this.required}
            @keydown=${this.keyboardNav}
            autocomplete="off"
            aria-owns="${this.id}-menu"
            aria-autocomplete="both"
            aria-describedby="${this.id}-description"
            aria-activedescendant="${this.currentFocusOption}"
            aria-expanded=${this.dropdownMenuVisibility && this.suggestions.length}
            role="combobox"
            aria-haspopup="listbox"
          />
          <button
            id="my-account-school-search-submit-button"
            class="btn btn-primary search-dropdown-icon"
            aria-label="See results for school search"
            ?disabled=${this.disabled}
            @click="${() => (this.dropdownMenuVisibility = !this.dropdownMenuVisibility)}"
          >
            ${this.suggestionStatus === ActionStatus.loading
              ? smallSpinner()
              : html` <uxt-icon icon="chevron-down"></uxt-icon>`}
          </button>
        </div>
        <div class="dropdown">
          <div
            id="${this.id}-menu"
            class="mt-2 dropdown-menu p-0 w-100"
            role="listbox"
            aria-label="school search options"
          >
            ${this.shouldShowSuggestions()
              ? html`
                  ${this.suggestions.length
                    ? html`
                        ${this.suggestions.length > 8 ? html`<div tab-index="0" aria-live="polite" class="border-bottom p-3 text-muted text-wrap">
                        Showing many results. Keep typing to narrow further.
                        </div>`: nothing }
                        <div class="list">
                          ${this.suggestions.map(
                            (suggestion, index) => html`
                              <button
                                id="active-option-${index}"
                                type="button"
                                class="text-capitalize dropdown-item py-1"
                                data-index=${index}
                                @mouseover=${this._mouseOverMenu}
                                @mouseout=${this._mouseOutMenu}
                                @click=${() => this.onSelectOption(suggestion)}
                                role="option"
                                tabindex="-1"
                              >
                                <span class="visually-hidden" id="suggestion-${index}">
                                  ${suggestion.name} ${suggestion?.address?.country || ''}
                                </span>
                                <span> ${suggestion.name?.toLowerCase() || ''} </span>
                                <br />
                                <span>
                                  ${suggestion.address?.city?.toLowerCase() || ''},
                                  ${suggestion.address?.state || ''}
                                </span>
                              </button>
                            `
                          )}
                          ${this.hasNextSuggestions ? html`
                            <button
                                id="load-more-option"
                                type="button"
                                class="dropdown-item py-1 fst-italic text-center border-top border-1 border-light"
                                data-index=${this.suggestions.length}
                                @mouseover=${this._mouseOverMenu}
                                @mouseout=${this._mouseOutMenu}
                                @click=${() => this.loadMore()}
                                ?disabled=${this.suggestionStatus === ActionStatus.loading}
                                role="option"
                                tabindex="-1"
                              >
                                <span class="visually-hidden" id="suggestion-${this.suggestions.length}">
                                  load more schools
                                </span>
                                ${this.suggestionStatus === ActionStatus.loading ? smallSpinner() : html`
                                  <span>Load more schools...</span>
                                `}
                              </button>
                          ` : nothing}
                        </div>
                      `
                    : html`<div tab-index="0" aria-live="polite" class="border-bottom p-3 text-muted text-wrap">
                        No Matches found
                      </div>`}
                `
              : html`
                  <div tab-index="0" aria-live="polite" class="border-bottom p-3 text-muted text-wrap">
                    No results found. Keep typing to narrow further.
                  </div>
                `}
          </div>
        </div>
        <span id="${this.id}-description" style="display: none;">
          When autocomplete Search suggestions are available use up and down arrows to review and enter to select.
        </span>
      </form>
    `;
  }


  private onSuggestionsLoaded() {
    if (this.lastIndex > 0) {
      if (this.selectFirstOnLoad) {
        const firstLoadedOption = this.shadowRoot?.getElementById(`active-option-${this.lastIndex}`) as HTMLButtonElement;
        this.activateOption(firstLoadedOption);
      } else {
        this.onLoadMoreResultsByClick();
      }
    }
  }

  private onLoadMoreResultsByClick() {
    this.inputEl?.focus();
    this.deactivateAllOptions();
  }

  private shouldShowSuggestions() {
    return (!this.requireTextInput || this.searchInput?.length > 2) &&
      this.hasNextSuggestions || !this.isLoadingInitialSuggestions();
  }

  private isLoadingInitialSuggestions() {
    return this.suggestionStatus === ActionStatus.loading && !this.suggestions.length;
  }

  private keyboardNav(e: KeyboardEvent) {
    const activtItem = this.shadowRoot?.querySelector(
      '.dropdown .dropdown-menu .list .dropdown-item.selected'
    ) as HTMLButtonElement;
    let prevItem = activtItem?.previousElementSibling as HTMLButtonElement;
    let nextItem = activtItem?.nextElementSibling as HTMLButtonElement;

    // if none of the item is active then make first item as next it
    if (!activtItem) {
      nextItem = this.shadowRoot?.querySelector('.dropdown .dropdown-menu .list .dropdown-item') as HTMLButtonElement;
    }

    switch (e.key) {
      case 'ArrowDown':
        this.showDropDownMenu();
        nextItem && this.activateOption(nextItem);
        break;
      case 'ArrowUp':
        prevItem && this.activateOption(prevItem);
        break;
      case 'Enter':
      case 'Tab':
        if (activtItem.id === 'load-more-option') {
          this.loadMore(true);
          e.preventDefault();
          break;
        }
        activtItem?.click();
        break;
      case 'Escape':
        this.hideDropDownMenu();
        break;
    }
  }

  private activateOption(option: HTMLButtonElement) {
    this.deactivateAllOptions();
    this.setSelectedOptionAttributes(option);
    this.currentFocusOption = option.id;
    this.ensureElementVisible(option);
    this.setSuggestionToPreview(option.dataset.index);
  }

  private setSelectedOptionAttributes(option: HTMLButtonElement) {
    option.classList.add('selected');
    option.setAttribute('aria-selected', 'true');
  }

  private setSuggestionToPreview(index) {
    this.preview = this.suggestions[+index];
    this.setSearchInput(this.preview);
  }

  private deactivateAllOptions() {
    this.shadowRoot
      ?.querySelectorAll('.dropdown .dropdown-menu .list .dropdown-item.selected')
      .forEach((i) => {
        i.classList.remove('selected');
        i.removeAttribute('aria-selected');
      });
  }

  onInputChange() {
    this.searchInput = (this.shadowRoot?.querySelector(`#${this.id}`) as HTMLInputElement)?.value;
    this.searchText = this.searchInput;
    this.hideErrorMessage();
    if (this.searchInput?.length >= 3) {
      this.clearSuggestions();
      this.dispatchSearch();
    } else {
      store.dispatch(resetSchoolResponse());
    }
  }

  private dispatchSearch(openDropdown = true) {
    store.dispatch(fetchSchools(this.getFetchSchoolRequest()))
      .unwrap()
      .then(() => openDropdown && this.showDropDownMenu());
  }

  private loadMore(selectFirstOnLoad: boolean = false) {
    this.selectFirstOnLoad = selectFirstOnLoad;
    if (this.hasNextSuggestions) {
      this.lastIndex = this.suggestions.length;
      this.dispatchSearch();
    }
  }

  private getFetchSchoolRequest() {
    return {
      countryCode: this.countryCode,
      stateCode: this.stateCode,
      zipCode: this.zipCode,
      searchText: this.searchText,
      segment: this.segment,
      start: this.suggestions.length
    };
  }

  private onFocusOut(){
    if (!this.mouseOverMenu){
      this.hideDropDownMenu();
    }
  }

  showDropDownMenu(_e?) {
    if (!_e?.target?.classList?.contains('search-dropdown-icon') && !this.dropdownMenuVisibility) {
      this.dropdownMenuVisibility = true;
    }
    clearTimeout(this.dropdownVisibilityTimeout);
  }

  hideDropDownMenu() {
    this.dropdownVisibilityTimeout = setTimeout(() => {
      this.dropdownMenuVisibility = false;
      this.updateInput();
      this.validateFields();
    }, 100);
  }

  updateInput() {
    if (this.selected) {
      this.setSearchInput(this.selected);
    } else if (this.searchText) {
      this.searchInput = this.searchText;
    } else {
      this.searchInput = '';
    }
  }

  setSearchInput(school: School) {
    if (school) {
      this.searchInput = `${school?.name?.toLowerCase()}${school?.address?.city ? ', ' + school?.address?.city.toLowerCase() : ''}`;
    }
  }

  private _mouseOverMenu(){
    this.mouseOverMenu = true;
  }

  private _mouseOutMenu(){
    this.mouseOverMenu = false;
  }

  async onSelectOption(suggestion: School) {
    this.selected = suggestion;
    this.searchText = '';
    this.setSearchInput(this.selected);
    this.dispatchEvent(
      new CustomEvent('onSchoolSelection', {
        detail: suggestion,
      })
    );
    this.hideDropDownMenu();
  }

  protected validationMessage() {
    return {
      [this.id]: this.errorMessage,
    };
  }

  private clearSuggestions() {
    this.suggestions = [];
    this.hasNextSuggestions = false;
  }

  protected resetSelectOption() {
    this.searchInput = '';
    this.clearSuggestions();
    this.dispatchEvent(
      new CustomEvent('onSchoolSelection', {
        detail: '',
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-school-selector': SchoolSelect;
  }
}
